<template>
    <div>
        <div class="columns heading-container is-variable is-0">
            <div class="column is-narrow is-paddingless">
                <router-link class="title is-4" :to="{name: 'spaces'}">
                    <i class="fas fa-bars has-text-white" style="opacity:0.8"></i> &nbsp;
                </router-link>
            </div>

            <div
                class="column is-paddingless dropdown"
                :class="{'is-active': show_spaces}"
                @mouseleave="show_spaces = false">

                <p @click="open_spaces_list"
                   class="title sidebar-title is-4 has-text-weight-semibold pointer">

                    {{space.name}}
                    <i class="icon fas fa-angle-down"></i>
                </p>

                <div class="dropdown-menu" id="dropdown-menu" role="menu">
                    <div class="dropdown-content">
                        <div v-if="spaces_list.loading" class="loader spaces-loader"></div>

                        <template v-else>
                            <b class="dropdown-item">Recent spaces</b>
                            <hr class="dropdown-divider"/>

                            <router-link
                                v-for="item in spaces_list.data.results"
                                :to="space_link(item.id)"
                                :key="item.id"
                                v-text="item.name"
                                class="dropdown-item"/>

                            <hr class="dropdown-divider"/>

                            <router-link :to="{name: 'spaces'}" class="dropdown-item" exact>
                                Spaces directory
                            </router-link>
                        </template>
                    </div>
                </div>
            </div>
        </div>

        <base-input
            right_icon="fas fa-search"
            name="search"
            class="search-box"
            placeholder="Search..."
            v-model="form.search"
            @keyup.enter="search"
            @right_icon_click="search"/>

        <div class="side-menu">
            <b class="heading">Navigation</b>

            <router-link :to="{name: 'content'}" class="menu-item" exact>
                <i class="icon fas fa-book-open"></i> All Notes
            </router-link>

            <div @dragenter.stop="private_drag_enter = true"
                 @dragleave.stop="private_drag_enter = false"
                 @dragover.stop="$event.preventDefault()"
                 @drop.prevent.stop="drop_to_private">
                <router-link
                    :to="{name: 'content', query: {'private': null}}"
                    :class="{'is-active': private_drag_enter}"
                    class="menu-item"
                    exec>

                    <i class="icon fas fa-user-shield"></i> Private
                </router-link>
            </div>

            <router-link :to="{name: 'content', query: {bookmarked: null}}" class="menu-item" exec>
                <i class="icon fas fa-star"></i> Starred
            </router-link>

            <router-link :to="{name: 'content', query: {tags_list: null}}" class="menu-item">
                <i class="icon fas fa-tags"></i> Tags
            </router-link>

            <router-link :to="{name: 'content', query: {trash: null}}" class="menu-item">
                <i class="icon fas fa-trash-alt"></i> Trash
            </router-link>
        </div>

        <flows-list :space="space"/>
        <saved-search-list :space="space"/>
        <topics :space="space"/>

        <div v-if="space_permission('INVITE')" class="side-menu">
            <b class="heading">More</b>

            <a @click="show_invite = true" class="menu-item">
                <i class="icon fas fa-user-plus"></i> Invite
            </a>

            <router-link
                v-if="space_permission('SPACE_UPDATE')"
                :to="{name: 'space_settings', params: {space: space.id}}"
                class="menu-item">

                <i class="icon fas fa-cog"></i> Settings
            </router-link>

            <invite-modal :visible="show_invite" :space="space" @close="show_invite = false"/>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import Topics from '../topic/Topics.vue';
import SavedSearchList from '../search/SavedSearchList.vue';
import InviteModal from './InviteModal.vue';
import FlowsList from '../flows/FlowsList.vue';
import { copy } from '../../utils/helpers';

export default {
    name: 'SpaceSidebar',
    props: ['space'],
    components: {
        FlowsList,
        SavedSearchList,
        InviteModal,
        Topics,
    },
    data() {
        return {
            show_invite: false,
            show_spaces: false,
            spaces_loaded: false,
            private_drag_enter: false,
            form: { search: this.$route.query.search || '' },
        };
    },
    computed: {
        ...mapState('spaces', { spaces_list: 'list' }),
        ...mapState('tree', ['moving_element']),
    },
    methods: {
        space_link(space) {
            const last_page = this.$store.state.spaces.last_page[space];
            return last_page || { name: 'content', params: { space } };
        },
        open_spaces_list() {
            this.show_spaces = !this.show_spaces;

            if (!this.spaces_loaded) {
                this.$store.dispatch('spaces/list');
                this.spaces_loaded = true;
            }
        },
        async drop_to_private() {
            this.private_drag_enter = false;
            if ('private' in this.$route.query) return;
            const element = copy(this.moving_element);

            if (element.kind === 'collection') {
                this.move_collection(element);
            } else if (element.kind === 'note') {
                this.move_note(element);
            }
        },
        async move_note(element) {
            await this.$store.dispatch('notes/update', {
                space_id: this.space.id,
                note_id: element.id,
                patch: true,
                data: { topic: null, is_private: true, collection: null },
            });

            if (element.source === 'tree') {
                this.$store.commit('tree/remove_item', element);
            } else {
                this.$store.commit('notes/change_note_topic', {
                    note_id: element.id,
                    topic: null,
                });
            }
        },
        async move_collection(element) {
            await this.$store.dispatch('tree/update_collection', {
                space_id: this.space.id,
                collection_id: element.id,
                data: { topic: null, parent: null, is_private: true },
            });

            this.$store.commit('tree/remove_item', element);
        },
        search() {
            if (this.form.search) {
                this.$router.push({
                    name: 'content',
                    params: this.$route.params,
                    query: { search: this.form.search },
                });
            }
        },
    },
};
</script>

<style scoped>
    .heading-container {
        margin-bottom: 0;
        margin-top: 0;
    }

    .sidebar-title {
        color: #ffffff;
        opacity: 0.8;
        width: 13rem;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        margin-bottom: 0.5rem;
    }

    .spaces-loader {
        margin-left: auto;
        margin-right: auto;
        font-size: 1.5rem;
    }

    .search-box {
        margin-top: 1rem;
    }

    .search-box >>> input {
        background-color: #e6f0ff;
    }

    .search-box >>> input::placeholder, .search-box >>> .icon {
        color: #555555;
    }
</style>
